/**
 * Schritt 1: Berechnungsparameter-Eingabe
 * Ermöglicht die Konfiguration von Steuerjahr, Familienstand und Kirchensteuer
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Header,
  FormField,
  Select,
  SpaceBetween,
  Button,
  Alert,
  ColumnLayout,
  Box,
  TextContent
} from '@cloudscape-design/components';

import { SteuerParameter } from '@/types';
import { 
  STEUERJAHRE, 
  FAMILIENSTAND_OPTIONEN, 
  KIRCHENSTEUER_OPTIONEN,
  SPARERFREIBETRAEGE,
  STANDARD_STEUERJAHR,
  STANDARD_FAMILIENSTAND,
  STANDARD_KIRCHENSTEUER
} from '@/utils/constants';

interface Schritt1Props {
  steuerParameter: SteuerParameter | null;
  onParameterUpdate: (parameter: SteuerParameter) => void;
}

/**
 * Komponente für die Eingabe der Berechnungsparameter
 */
const Schritt1: React.FC<Schritt1Props> = ({ steuerParameter, onParameterUpdate }) => {
  // Lokaler State für die Formular-Eingaben mit Standardwerten
  const [steuerjahr, setSteuerjahr] = useState<number | null>(steuerParameter?.steuerjahr ?? STANDARD_STEUERJAHR);
  const [familienstand, setFamilienstand] = useState<string>(steuerParameter?.familienstand ?? STANDARD_FAMILIENSTAND);
  const [kirchensteuer, setKirchensteuer] = useState<string>(steuerParameter?.kirchensteuer ?? STANDARD_KIRCHENSTEUER);
  const [istBearbeitungsmodus, setIstBearbeitungsmodus] = useState<boolean>(false);

  // Initialisierung mit vorhandenen Parametern (deferred to avoid synchronous setState in effect)
  useEffect(() => {
    if (steuerParameter) {
      queueMicrotask(() => {
        setSteuerjahr(steuerParameter.steuerjahr);
        setFamilienstand(steuerParameter.familienstand);
        setKirchensteuer(steuerParameter.kirchensteuer);
      });
    } else {
      const standardParameter: SteuerParameter = {
        steuerjahr: STANDARD_STEUERJAHR,
        familienstand: STANDARD_FAMILIENSTAND as 'alleinstehend' | 'verheiratet',
        kirchensteuer: STANDARD_KIRCHENSTEUER as 'keine' | 'baden-wuerttemberg-bayern' | 'andere-bundeslaender'
      };
      onParameterUpdate(standardParameter);
    }
  }, [steuerParameter, onParameterUpdate]);

  // Abgeleitete Werte statt Effect (vermeidet set-state-in-effect)
  const istFormularGueltig = useMemo(() => !!(steuerjahr && familienstand && kirchensteuer), [steuerjahr, familienstand, kirchensteuer]);
  const sparerfreibetrag = useMemo(() => {
    if (familienstand === 'alleinstehend') return SPARERFREIBETRAEGE.alleinstehend;
    if (familienstand === 'verheiratet') return SPARERFREIBETRAEGE.verheiratet;
    return 0;
  }, [familienstand]);

  /**
   * Handler für das Speichern der Parameter
   */
  const handleParameterSpeichern = () => {
    if (istFormularGueltig && steuerjahr && familienstand && kirchensteuer) {
      const parameter: SteuerParameter = {
        steuerjahr,
        familienstand: familienstand as 'alleinstehend' | 'verheiratet',
        kirchensteuer: kirchensteuer as 'keine' | 'baden-wuerttemberg-bayern' | 'andere-bundeslaender'
      };
      onParameterUpdate(parameter);
    }
  };

  // Dropdown-Optionen für Steuerjahr
  const steuerjahrOptionen = STEUERJAHRE.map(jahr => ({
    label: jahr.toString(),
    value: jahr.toString()
  }));

  // Aktuelle Kirchensteuer-Konfiguration
  const aktuelleKirchensteuer = KIRCHENSTEUER_OPTIONEN.find(k => k.key === kirchensteuer);

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Geben Sie die allgemeinen Parameter für diese Simulation ein."
          actions={
            !istBearbeitungsmodus ? (
              <Button
                onClick={() => setIstBearbeitungsmodus(true)}
                iconName="edit"
              >
                Bearbeiten
              </Button>
            ) : undefined
          }
        >
          Berechnungsparameter
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert
          type="warning"
          dismissAriaLabel="Schließen"
          header="Wichtiger rechtlicher Hinweis"
        >
          Diese Werte basieren auf gesetzlich festgelegten Pauschalen und Standardannahmen. Sie dienen ausschließlich der Simulation, sind nicht auf Ihre persönliche steuerliche Situation zugeschnitten und ersetzen keine steuerliche Beratung. Die eingegebenen Werte werden nicht auf Richtigkeit geprüft und haben keinen Einfluss auf eine amtliche Steuerberechnung.
        </Alert>

        {istBearbeitungsmodus ? (
          <SpaceBetween direction="vertical" size="l">
            <ColumnLayout columns={2} borders="vertical">
              <SpaceBetween direction="vertical" size="m">
                <FormField
                  label="Berechnungsjahr"
                  description="Wählen Sie das relevante Jahr für diese Simulation"
                >
                  <Select
                    selectedOption={
                      steuerjahr ? { label: steuerjahr.toString(), value: steuerjahr.toString() } : null
                    }
                    onChange={({ detail }) => {
                      setSteuerjahr(detail.selectedOption ? parseInt(detail.selectedOption.value ?? '0') : null);
                    }}
                    options={steuerjahrOptionen}
                    placeholder="Steuerjahr auswählen..."
                    expandToViewport
                  />
                </FormField>

                <FormField
                  label="Familienstand"
                  description="Bestimmt den gesetzlichen Sparer-Pauschbetrag"
                >
                  <Select
                    selectedOption={
                      familienstand ? FAMILIENSTAND_OPTIONEN.find(opt => opt.value === familienstand) || null : null
                    }
                    onChange={({ detail }) => {
                      setFamilienstand(detail.selectedOption ? (detail.selectedOption.value ?? '') : '');
                    }}
                    options={FAMILIENSTAND_OPTIONEN.map(opt => ({
                      label: opt.label,
                      value: opt.value
                    }))}
                    placeholder="Familienstand auswählen..."
                    expandToViewport
                  />
                </FormField>

                <FormField
                  label="Kirchensteuer"
                  description="Pauschaler Kirchensteuersatz für die Berechnung"
                >
                  <Select
                    selectedOption={
                      kirchensteuer ? KIRCHENSTEUER_OPTIONEN.find(opt => opt.key === kirchensteuer) || null : null
                    }
                    onChange={({ detail }) => {
                      setKirchensteuer(detail.selectedOption ? (detail.selectedOption as any).key : '');
                    }}
                    options={KIRCHENSTEUER_OPTIONEN.map(opt => ({
                      label: opt.label,
                      value: opt.key,
                      key: opt.key
                    }))}
                    placeholder="Kirchensteuer auswählen..."
                    expandToViewport
                  />
                </FormField>
              </SpaceBetween>

              <SpaceBetween direction="vertical" size="m">
                <Box variant="h3">Vorschau der Berechnungsparameter</Box>
                {steuerjahr && (
                  <Box variant="div">
                    <ColumnLayout columns={2} borders="horizontal">
                      <Box>
                        <TextContent>
                          <p><strong>Berechnungsjahr</strong></p>
                          <p style={{ fontSize: '18px', color: '#0972d3' }}>{steuerjahr}</p>
                        </TextContent>
                      </Box>
                      <Box>
                        <TextContent>
                          <p><strong>Abrechnungsperiode</strong></p>
                          <p>Kalenderjahr {steuerjahr}</p>
                        </TextContent>
                      </Box>
                    </ColumnLayout>
                  </Box>
                )}

                {familienstand && (
                  <Box variant="div">
                    <ColumnLayout columns={2} borders="horizontal">
                      <Box>
                        <TextContent>
                          <p><strong>Familienstand</strong></p>
                          <p style={{ fontSize: '18px', color: '#0972d3' }}>
                            {FAMILIENSTAND_OPTIONEN.find(opt => opt.value === familienstand)?.label}
                          </p>
                        </TextContent>
                      </Box>
                      <Box>
                        <TextContent>
                          <p><strong>Sparer-Pauschbetrag</strong></p>
                          <p style={{ fontSize: '18px', color: '#037f0c' }}>
                            {sparerfreibetrag.toLocaleString('de-DE')} EUR (gesetzlicher Standardwert)
                          </p>
                        </TextContent>
                      </Box>
                    </ColumnLayout>
                  </Box>
                )}

                {aktuelleKirchensteuer && (
                  <Box variant="div">
                    <ColumnLayout columns={2} borders="horizontal">
                      <Box>
                        <TextContent>
                          <p><strong>Kirchensteuer</strong></p>
                          <p style={{ fontSize: '18px', color: '#0972d3' }}>
                            {aktuelleKirchensteuer.label}
                          </p>
                        </TextContent>
                      </Box>
                      <Box>
                        <TextContent>
                          <p><strong>Kirchensteuersatz</strong></p>
                          <p style={{ fontSize: '18px' }}>
                            {aktuelleKirchensteuer.prozentsatz}%
                          </p>
                        </TextContent>
                      </Box>
                    </ColumnLayout>
                  </Box>
                )}
              </SpaceBetween>
            </ColumnLayout>

            <SpaceBetween direction="horizontal" size="m">
              <Button
                variant="primary"
                onClick={() => {
                  handleParameterSpeichern();
                  setIstBearbeitungsmodus(false);
                }}
                disabled={!istFormularGueltig}
              >
                Speichern
              </Button>
              <Button
                onClick={() => setIstBearbeitungsmodus(false)}
              >
                Abbrechen
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        ) : (
          <SpaceBetween direction="vertical" size="m">
            <SpaceBetween direction="vertical" size="m">
              {steuerjahr && (
                <Box variant="div">
                  <ColumnLayout columns={2} borders="horizontal">
                    <Box>
                      <TextContent>
                        <p><strong>Berechnungsjahr</strong></p>
                        <p style={{ fontSize: '18px', color: '#0972d3' }}>{steuerjahr}</p>
                      </TextContent>
                    </Box>
                    <Box>
                      {/* Abrechnungsperiode removed as redundant */}
                    </Box>
                  </ColumnLayout>
                </Box>
              )}

              {familienstand && aktuelleKirchensteuer && (
                <Box variant="div">
                  <ColumnLayout columns={2} borders="horizontal">
                    <Box>
                      <TextContent>
                        <p><strong>Familienstand</strong></p>
                        <p style={{ fontSize: '18px', color: '#0972d3' }}>
                          {FAMILIENSTAND_OPTIONEN.find(opt => opt.value === (familienstand || STANDARD_FAMILIENSTAND))?.label}
                        </p>
                      </TextContent>
                    </Box>
                    <Box>
                      <TextContent>
                                                  <p><strong>Pauschaler Gesamtsatz für Kapitalerträge</strong></p>
                        <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#d13212', margin: '0 0 8px 0' }}>
                          {(() => {
                            // Use exact values from screenshot for display
                            if (aktuelleKirchensteuer.key === 'baden-wuerttemberg-bayern') {
                              return '27.82';
                            } else if (aktuelleKirchensteuer.key === 'andere-bundeslaender') {
                              return '27.99';
                            } else {
                              return '26.38';
                            }
                          })()}%
                        </p>
                        <p style={{ fontSize: '14px', color: '#5f6b7a', margin: 0 }}>
                          {(() => {
                            // Use exact values from screenshot for display
                            if (aktuelleKirchensteuer.key === 'baden-wuerttemberg-bayern') {
                              return 'KapESt (24.51%) + SolZ (1.35%) + KiSt (1.96%)';
                            } else if (aktuelleKirchensteuer.key === 'andere-bundeslaender') {
                              return 'KapESt (24.45%) + SolZ (1.34%) + KiSt (2.20%)';
                            } else {
                              return 'KapESt (25.00%) + SolZ (1.38%) + KiSt (0.00%)';
                            }
                          })()}
                        </p>
                      </TextContent>
                    </Box>
                  </ColumnLayout>
                </Box>
              )}

              {aktuelleKirchensteuer && (
                <Box variant="div">
                  <ColumnLayout columns={2} borders="horizontal">
                    <Box>
                      <TextContent>
                        <p><strong>Kirchensteuer</strong></p>
                        <p style={{ fontSize: '18px', color: '#0972d3' }}>
                          {aktuelleKirchensteuer.label}
                        </p>
                      </TextContent>
                    </Box>
                    <Box>
                      <TextContent>
                        <p><strong>Sparer-Pauschbetrag</strong></p>
                        <p style={{ fontSize: '18px', color: '#037f0c' }}>
                          {sparerfreibetrag.toLocaleString('de-DE')} EUR (gesetzlicher Standardwert)
                        </p>
                      </TextContent>
                    </Box>
                  </ColumnLayout>
                </Box>
              )}
            </SpaceBetween>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default Schritt1;